import '@patsnap-ui/icon/assets/solid/CloseBig.svg'
import { computed, defineComponent, getCurrentInstance, onBeforeUnmount, ref, watch } from '@vue/composition-api'
import { GIcon } from 'pharmsnapMF_shared/components'
import { showSingleToast } from 'pharmsnapMF_shared/utils'
import {
  diagnosticRecordingEndState,
  diagnosticRecordingState,
  discardDiagnosticsRecording,
  downloadDiagnosticReport,
  getDiagnosticRecordingStartedAt,
} from '../../features/diagnostics'
import $style from './DiagnosticRecordingControl.module.scss'

const DEFAULT_CONTROL_WIDTH = 200
const DEFAULT_CONTROL_HEIGHT = 56
const VIEWPORT_PADDING = 12

type ControlPosition = {
  left: number
  top: number
}

type ControlSize = {
  width: number
  height: number
}

function formatElapsedTime(elapsedMs: number): string {
  const totalSeconds = Math.max(Math.floor(elapsedMs / 1000), 0)
  return `${Math.floor(totalSeconds / 60)}:${String(totalSeconds % 60).padStart(2, '0')}`
}

export const DiagnosticRecordingControl = defineComponent({
  name: 'DiagnosticRecordingControl',
  i18n: {
    messages: {
      cn: {
        diagnosticRecording: {
          recording: '正在记录',
          finished: '记录已结束',
          elapsed: '已记录 {time}',
          finishAndDownload: '结束并下载',
          download: '下载诊断文件',
          discard: '关闭并丢弃诊断记录',
          timeout: '记录已结束，请下载诊断文件',
          downloadSuccess: '诊断文件已下载，请发送给客服或技术支持',
          exportFailed: '诊断文件生成失败，请重试',
        },
      },
      en: {
        diagnosticRecording: {
          recording: 'Recording',
          finished: 'Recording finished',
          elapsed: '{time} recorded',
          finishAndDownload: 'Finish and download',
          download: 'Download diagnostic file',
          discard: 'Close and discard diagnostic recording',
          timeout: 'Recording finished. Download the diagnostic file.',
          downloadSuccess: 'Diagnostic file downloaded. Please send it to customer service or technical support.',
          exportFailed: 'Failed to generate the diagnostic file. Please try again.',
        },
      },
    },
  },
  setup() {
    const instance = getCurrentInstance()
    const now = ref(Date.now())
    const isExporting = ref(false)
    const isDragging = ref(false)
    const position = ref<ControlPosition | undefined>()
    const suppressClick = ref(false)
    const controlSize = ref<ControlSize>({ width: DEFAULT_CONTROL_WIDTH, height: DEFAULT_CONTROL_HEIGHT })
    let timer: ReturnType<typeof setInterval> | undefined
    let dragOffset: ControlPosition | undefined
    let dragStartPoint: ControlPosition | undefined

    const controlStyle = computed(() => {
      if (!position.value) return undefined
      return {
        left: `${position.value.left}px`,
        top: `${position.value.top}px`,
        transform: 'none',
      }
    })

    const elapsedTime = computed(() => {
      const isRecording = diagnosticRecordingState.status === 'recording'
      if (!isRecording) return formatElapsedTime(0)
      const startedAt = getDiagnosticRecordingStartedAt()
      return formatElapsedTime(now.value - (startedAt || now.value))
    })

    const startTimer = () => {
      if (timer) clearInterval(timer)
      timer = setInterval(() => {
        now.value = Date.now()
      }, 1000)
    }
    const stopTimer = () => {
      if (!timer) return
      clearInterval(timer)
      timer = undefined
    }
    const clampPosition = (nextPosition: ControlPosition): ControlPosition => ({
      left: Math.min(
        Math.max(nextPosition.left, VIEWPORT_PADDING),
        Math.max(VIEWPORT_PADDING, window.innerWidth - controlSize.value.width - VIEWPORT_PADDING)
      ),
      top: Math.min(
        Math.max(nextPosition.top, VIEWPORT_PADDING),
        Math.max(VIEWPORT_PADDING, window.innerHeight - controlSize.value.height - VIEWPORT_PADDING)
      ),
    })
    const handleMouseMove = (event: MouseEvent) => {
      if (!dragOffset || !dragStartPoint) return
      const nextPosition = clampPosition({
        left: event.clientX - dragOffset.left,
        top: event.clientY - dragOffset.top,
      })
      if (Math.abs(event.clientX - dragStartPoint.left) > 4 || Math.abs(event.clientY - dragStartPoint.top) > 4) {
        suppressClick.value = true
      }
      position.value = nextPosition
    }
    const handleMouseUp = () => {
      isDragging.value = false
      dragOffset = undefined
      dragStartPoint = undefined
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
      if (suppressClick.value) {
        window.setTimeout(() => {
          suppressClick.value = false
        }, 0)
      }
    }
    const handleMouseDown = (event: MouseEvent) => {
      if (event.button !== 0) return
      const target = event.currentTarget as HTMLButtonElement
      const rect = target.getBoundingClientRect()
      controlSize.value = { width: rect.width, height: rect.height }
      isDragging.value = true
      suppressClick.value = false
      dragOffset = {
        left: event.clientX - rect.left,
        top: event.clientY - rect.top,
      }
      dragStartPoint = {
        left: event.clientX,
        top: event.clientY,
      }
      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)
    }
    const handleFinishAndDownload = async () => {
      if (isExporting.value || suppressClick.value) return
      isExporting.value = true
      try {
        await downloadDiagnosticReport()
        showSingleToast({ message: instance?.proxy.$t('diagnosticRecording.downloadSuccess') as string, type: 'success' })
      } catch {
        showSingleToast({ message: instance?.proxy.$t('diagnosticRecording.exportFailed') as string, type: 'error' })
      } finally {
        discardDiagnosticsRecording()
        isExporting.value = false
      }
    }
    const handleDiscard = (event: MouseEvent) => {
      event.preventDefault()
      event.stopPropagation()
      discardDiagnosticsRecording()
    }

    watch(
      diagnosticRecordingState,
      (state) => {
        if (state.status === 'recording') {
          now.value = Date.now()
          startTimer()
          return
        }
        stopTimer()
      },
      { immediate: true }
    )
    watch(diagnosticRecordingEndState, (state) => {
      if (state.reason === 'timeout') {
        showSingleToast({ message: instance?.proxy.$t('diagnosticRecording.timeout') as string, type: 'warning' })
      }
    })
    onBeforeUnmount(() => {
      stopTimer()
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    })

    return {
      diagnosticRecordingState,
      handleFinishAndDownload,
      handleDiscard,
      handleMouseDown,
      isDragging,
      isExporting,
      elapsedTime,
      controlStyle,
    }
  },
  render() {
    const isRecording = this.diagnosticRecordingState.status === 'recording'
    return (
      <div v-show={this.diagnosticRecordingState.status !== 'idle'} class={$style.container} style={this.controlStyle}>
        <button
          class={[$style.control, this.$i18n.locale === 'cn' && $style.chinese, !isRecording && $style.finished, this.isDragging && $style.dragging]}
          disabled={this.isExporting}
          onClick={this.handleFinishAndDownload}
          onMousedown={this.handleMouseDown}
        >
          <span class={$style.recordingContent}>
            <span class={$style.recordingTitle}>
              <span>{this.$t(isRecording ? 'diagnosticRecording.recording' : 'diagnosticRecording.finished')}</span>
            </span>
            {isRecording && <span class={$style.remaining}>{this.$t('diagnosticRecording.elapsed', { time: this.elapsedTime })}</span>}
          </span>
          <span class={$style.finishContent}>{this.$t(isRecording ? 'diagnosticRecording.finishAndDownload' : 'diagnosticRecording.download')}</span>
        </button>
        {!isRecording && (
          <button
            class={$style.closeButton}
            aria-label={this.$t('diagnosticRecording.discard') as string}
            title={this.$t('diagnosticRecording.discard') as string}
            onClick={this.handleDiscard}
          >
            <GIcon svgName="SolidCloseBig" size={12}></GIcon>
          </button>
        )}
      </div>
    )
  },
})
